/**
 * OCTOPUS Dashboard - FASE 3: Widget Library
 * DataChart - Gráfico temporal de linha para histórico de valores
 * 
 * Estilo: Marítimo/Industrial moderno, tema dark
 */

import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import { useTimezone } from '@/contexts/TimezoneContext';

const DataChart = ({
  value = [], // Array de {timestamp, value}
  label = '',
  unit = '',
  showLabel = true,
  showGrid = true,
  showArea = true,
  color = '#22c55e',
  width = 400,
  height = 200,
  decimalPlaces = 1,
}) => {
  const { formatTime } = useTimezone();
  // Preparar dados para o gráfico
  const chartData = useMemo(() => {
    if (!Array.isArray(value) || value.length === 0) {
      // Dados de exemplo para preview
      const now = new Date();
      return Array.from({ length: 20 }, (_, i) => ({
        time: formatTime(new Date(now.getTime() - (19 - i) * 60000), { omitSeconds: true }),
        value: 50 + Math.sin(i * 0.5) * 20 + Math.random() * 5,
      }));
    }

    return value.map(point => ({
      time: point.timestamp 
        ? formatTime(point.timestamp, { omitSeconds: true })
        : '--',
      value: typeof point.value === 'number' ? point.value : 0,
    }));
  }, [formatTime, value]);

  // Calcular min/max para o domínio Y
  const yDomain = useMemo(() => {
    if (chartData.length === 0) return [0, 100];
    const values = chartData.map(d => d.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.1 || 10;
    return [Math.floor(min - padding), Math.ceil(max + padding)];
  }, [chartData]);

  // Tooltip customizado
  const CustomTooltip = ({ active, payload, labelValue }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-600 rounded-lg p-2 shadow-lg">
          <p className="text-gray-400 text-xs">{labelValue}</p>
          <p className="text-white font-mono font-bold">
            {payload[0].value.toFixed(decimalPlaces)} {unit}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div 
      className="flex flex-col bg-gray-900 rounded-lg border border-gray-700 p-3"
      style={{ width, height: height + 40 }}
    >
      {/* Header */}
      {showLabel && label && (
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-300 text-sm font-medium">
            {label}
          </span>
          {unit && (
            <span className="text-gray-500 text-xs">
              ({unit})
            </span>
          )}
        </div>
      )}

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={height}>
        {showArea ? (
          <AreaChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            {showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#374151" 
                opacity={0.5}
              />
            )}
            <XAxis 
              dataKey="time" 
              stroke="#6b7280" 
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: '#374151' }}
            />
            <YAxis 
              domain={yDomain}
              stroke="#6b7280" 
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: '#374151' }}
              tickFormatter={v => v.toFixed(0)}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: '#6b7280', strokeDasharray: '3 3' }}
            />
            <defs>
              <linearGradient id={`gradient-${label}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${label})`}
              dot={false}
              activeDot={{ 
                r: 4, 
                stroke: color, 
                strokeWidth: 2,
                fill: '#1f2937',
              }}
            />
          </AreaChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
            {showGrid && (
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#374151" 
                opacity={0.5}
              />
            )}
            <XAxis 
              dataKey="time" 
              stroke="#6b7280" 
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: '#374151' }}
            />
            <YAxis 
              domain={yDomain}
              stroke="#6b7280" 
              fontSize={10}
              tickLine={false}
              axisLine={{ stroke: '#374151' }}
              tickFormatter={v => v.toFixed(0)}
            />
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ stroke: '#6b7280', strokeDasharray: '3 3' }}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2}
              dot={false}
              activeDot={{ 
                r: 4, 
                stroke: color, 
                strokeWidth: 2,
                fill: '#1f2937',
              }}
            />
          </LineChart>
        )}
      </ResponsiveContainer>

      {/* Valor atual (último ponto) */}
      {chartData.length > 0 && (
        <div className="flex items-center justify-end gap-2 mt-1">
          <span className="text-gray-500 text-xs">Atual:</span>
          <span 
            className="font-mono font-bold text-sm"
            style={{ color }}
          >
            {chartData[chartData.length - 1].value.toFixed(decimalPlaces)} {unit}
          </span>
        </div>
      )}
    </div>
  );
};

export default DataChart;
